/**
 * NavigationItem Component - Shared navigation link styling
 * 
 * Reusable component for menu items and accordion items with consistent:
 * - Active state styling
 * - Hover effects
 * - Shine animations
 * - Accent bars
 * 
 * @param to - Route path
 * @param label - Display text
 * @param icon - Optional emoji or icon
 * @param variant - Style variant: 'menu' (top-level) or 'accordion' (nested)
 * @param onClick - Optional click handler
 * 
 * @example
 * <NavigationItem to="/Learn" label="Learn" icon="📚" variant="menu" />
 */

import React from "react";
import { NavLink } from "react-router-dom";
import styles from "../styles/NavigationItem.module.css";

interface NavigationItemProps {
    readonly to: string;
    readonly label: string;
    readonly icon?: string;
    readonly variant?: "menu" | "accordion";
    readonly end?: boolean;
    readonly onClick?: () => void;
}

const NavigationItem = React.memo(function NavigationItem({
    to,
    label,
    icon,
    variant = "menu",
    end = false,
    onClick,
}: NavigationItemProps) {
    return (
        <NavLink
            to={to}
            end={end}
            className={({ isActive }) =>
                `${styles.navItem} ${styles[variant]} ${isActive ? styles.active : ""}`
            }
            onClick={onClick}
        >
            {icon && <span className={styles.icon}>{icon}</span>}
            <span className={styles.label}>{label}</span>
        </NavLink>
    );
});

export default NavigationItem;
